import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import * as XLSX from "xlsx";
import { z } from "zod";
import { Button } from "../../Components/ui/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../Components/ui/Form";
import { Input } from "../../Components/ui/Input";
import { MdOutlineDeleteOutline } from "react-icons/md";
import { createUser } from "../../lib/PB/Users";

export const Route = createFileRoute("/Users/Invite")({
  component: Invite,
});

const inviteSchema = z.object({
  users: z.array(
    z.object({
      email: z
        .string()
        .min(1, { message: "must provide an email" })
        .email({ message: "Invalid email format" }),
    })
  ),
});

export default function Invite() {
  const form = useForm<z.infer<typeof inviteSchema>>({
    resolver: zodResolver(inviteSchema),
  });

  const { fields, append, remove, update } = useFieldArray<
    z.infer<typeof inviteSchema>
  >({
    control: form.control,
    name: "users",
  });
  const onSubmit = async (values: z.infer<typeof inviteSchema>) => {
    console.log(values);
    for (let u = 0; u < values.users.length; u++) {
      const user = values.users[u];
      await createUser(user.email);
      update(u, { email: "" });
    }
  };

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [addedEmails, setAddedEmails] = useState([]);

  const appendedOnceRef = useRef(false);
  useEffect(() => {
    if (!appendedOnceRef.current) {
      append({ email: "" }, { shouldFocus: true });
      appendedOnceRef.current = true;
    }
  }, []);

  useEffect(() => {
    addedEmails.forEach((addedEmail) => {
      append({ email: addedEmail.email });
    });
    if (addedEmails.length === 0 && fields.length > 1) {
      for (let i = fields.length - 1; i > 0; i--) {
        remove(i);
      }
    }
  }, [addedEmails]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result instanceof ArrayBuffer) {
          const workbook = XLSX.read(e.target.result, { type: "buffer" });
          const sheetNameList = workbook.SheetNames;
          const jsonData = XLSX.utils.sheet_to_json(
            workbook.Sheets[sheetNameList[0]]
          );
          setAddedEmails(jsonData);
        } else {
          console.error("File content is not a ArrayBuffer");
        }
      };
      reader.onerror = (err) => {
        console.error("Error reading file:", err);
      };
      reader.readAsArrayBuffer(file);
    }
  };

  return (
    <div>
      <div className="flex justify-end">
        <input
          id="hidden-file-input"
          type="file"
          accept="csv, xlsx"
          onChange={handleFileUpload}
          style={{ display: "none" }}
        />
        <Button
          type="button"
          variant={"outline"}
          className="m-3"
          onClick={() => document.getElementById("hidden-file-input")?.click()}
        >
          {selectedFile?.name ? (
            <>
              <span className="flex flex-nowrap gap-1 items-center">
                {selectedFile?.name}
                <MdOutlineDeleteOutline
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFile(null);
                    setAddedEmails([]);
                  }}
                  className="hover:bg-slate-400 hover:rounded"
                />
              </span>
            </>
          ) : (
            "add emails using CSV file"
          )}
        </Button>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-8 bg-white shadow-md rounded px-8 pt-6 pb-8 mb-4"
        >
          {fields.map((emailField, i) => {
            return (
              <div
                key={emailField.id}
                className="flex justify-between items-center"
              >
                <FormField
                  control={form.control}
                  name={`users.${i}`}
                  defaultValue={emailField}
                  render={() => {
                    return (
                      <FormItem className="flex items-center gap-3 w-[80%]">
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="Email"
                            {...form.register(`users.${i}.email`)}
                          />
                        </FormControl>
                        <FormMessage>
                          {form.formState.errors.users?.[i]?.email?.message}
                        </FormMessage>
                      </FormItem>
                    );
                  }}
                />
                <MdOutlineDeleteOutline
                  className="w-[20%] text-red-700 cursor-pointer"
                  onClick={() => fields.length > 1 && remove(i)}
                />
              </div>
            );
          })}
          <Button type="submit" variant={"default"} className="m-5">
            Submit
          </Button>
        </form>
      </Form>
    </div>
  );
}
